import { Stack, Group, Text, Box, Button, Badge } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate } from '../../../../lib/format';
import { TenantName } from '../../../../components/TenantName';
import { CopyableId } from '../../../../components/CopyableId';
import { Money } from '../../components/cells';
import { RowMenu } from '../../../../components/RowMenu';
import {
  statusLabel, statusTone, validationLabel, validationTone,
  type InvoiceRowActions
} from './InvoicesTable';
import { useT } from '../../../../i18n/I18nProvider';

interface Props extends InvoiceRowActions {
  records: admin.AdminInvoice[];
}

/** Vertical list view: the responsive answer to a ten-column invoice table. */
export function InvoicesList({ records, onView, onValidate, onRegenerate }: Props) {
  const t = useT();
  return (
    <Stack gap="xs">
      {records.map((r) => (
        <div key={r.id} className="rr-list-row" style={{ cursor: 'default' }}>
          <Group gap="md" align="center" wrap="wrap" style={{ width: '100%' }}>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="nowrap">
                <CopyableId value={r.invoice_number} label={t('billing.invoices.copyNumber')} chars={24} />
                <TenantName id={r.organisation_id} subtle />
              </Group>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('billing.invoices.period', { from: formatDate(r.period_start), to: formatDate(r.period_end) })}
              </Text>
            </Stack>
            <Group gap="xs" align="center" wrap="nowrap">
              <StatusBadge status={statusTone(r.status)}>{statusLabel(t, r.status)}</StatusBadge>
              <StatusBadge status={validationTone(r.validation_status)}>
                {validationLabel(t, r.validation_status)}
              </StatusBadge>
              {r.validation_validator && <Badge size="xs" variant="light">{r.validation_validator.toUpperCase()}</Badge>}
            </Group>
            <Box style={{ minWidth: 120, textAlign: 'right' }}>
              <Money minor={r.total_minor} currency={r.currency} />
            </Box>
            <Box style={{ flexShrink: 0 }}>
              <Group gap="xs" wrap="nowrap">
                <Button size="xs" variant="default" onClick={() => onView(r)}>
                  {t('action.view')}
                </Button>
                <RowMenu
                  actions={[
                    { id: 'validate', label: t('billing.invoices.actions.validate'), onSelect: () => onValidate(r) },
                    { id: 'regenerate', label: t('billing.invoices.actions.regenerate'), onSelect: () => onRegenerate(r), destructive: true }
                  ]}
                />
              </Group>
            </Box>
          </Group>
        </div>
      ))}
    </Stack>
  );
}
